// ---------------------------------------------------------------------------
// Quality Tab — NPS + EARN model
// ---------------------------------------------------------------------------

export type QualityEventType = 'nps' | 'earn';
export type QualitySource = 'fa' | 'ai' | 'system';
export type CaseStatus = 'active' | 'closed';
export type QualityRisk = 'good' | 'watch' | 'at_risk' | 'none';
export type NpsSurveyChannel = 'email' | 'chat' | 'call_transfer' | 'manual';

// ---------------------------------------------------------------------------
// EARN model
// ---------------------------------------------------------------------------

export type EarnDimensionId =
  | 'connection_trust'
  | 'needs_discovery'
  | 'pathway_execution'
  | 'future_growth'
  | 'delivered_value';

export type EarnAttributeStatus = 'strong' | 'caution' | 'risk' | 'unknown';

export interface EarnAttribute {
  id: string;
  label: string;
  status: EarnAttributeStatus;
  notes?: string;
}

export interface EarnDimension {
  id: EarnDimensionId;
  label: string;
  score: number; // 0–100
  attributes: EarnAttribute[];
}

export type AssessedEntityType = 'fa' | 'ai';

export interface EarnEvent {
  id: string;
  type: 'earn';
  timestamp: string;
  caseStatus: CaseStatus;
  source: QualitySource;
  score0to100: number;
  assessedEntityType: AssessedEntityType;
  assessedEntityName: string;
  dimensions: EarnDimension[];
  aiSummaryBullets: string[];
  recommendations: string[];
  verbatim?: string;
}

// ---------------------------------------------------------------------------
// NPS model
// ---------------------------------------------------------------------------

export type NpsCategory = 'promoter' | 'passive' | 'detractor';

export interface NpsEvent {
  id: string;
  type: 'nps';
  timestamp: string;
  caseStatus: CaseStatus;
  source: QualitySource;
  score: number; // 0–10
  category: NpsCategory;
  channel: NpsSurveyChannel;
  verbatim?: string;
  aiSummaryBullets: string[];
  suppressed?: boolean;
}

// ---------------------------------------------------------------------------
// Unified timeline
// ---------------------------------------------------------------------------

export type QualityTimelineEvent = NpsEvent | EarnEvent;

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export type QualityTimeWindow = '30d' | '90d' | '180d' | '1y' | 'all';

export interface QualityState {
  npsEvents: NpsEvent[];
  earnEvents: EarnEvent[];
  /** Pre-sorted (desc by timestamp, stable by id) merged view. */
  timeline: QualityTimelineEvent[];
  latestNps: NpsEvent | null;
  latestEarn: EarnEvent | null;
  qualityRisk: QualityRisk;
  activeTimeWindow: QualityTimeWindow;
}

// ---------------------------------------------------------------------------
// EARN dimension catalogue (static structure)
// ---------------------------------------------------------------------------

export const EARN_DIMENSION_CATALOGUE: {
  id: EarnDimensionId;
  label: string;
  attributes: string[];
}[] = [
  {
    id: 'connection_trust',
    label: 'Connection & Trust',
    attributes: ['Empathetic Listening', 'Agency Building', 'Personalization', 'Client Trust'],
  },
  {
    id: 'needs_discovery',
    label: 'Needs Discovery',
    attributes: ['Immediate Goal', 'Motivators', 'Barriers', 'Client Alignment'],
  },
  {
    id: 'pathway_execution',
    label: 'Pathway Execution',
    attributes: ['Building', 'Selecting', 'Planning', 'Client Confirmation'],
  },
  {
    id: 'future_growth',
    label: 'Future Growth Positioning',
    attributes: ['Prompt', 'Position', 'Plan', 'Client Interest'],
  },
  {
    id: 'delivered_value',
    label: 'Delivered Value',
    attributes: ['Emotional Value', 'Financial Value', 'Relevancy', 'Client Affirmation'],
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function npsCategory(score: number): NpsCategory {
  if (score >= 9) return 'promoter';
  if (score >= 7) return 'passive';
  return 'detractor';
}

export function deriveQualityRisk(
  latestNps: NpsEvent | null,
  latestEarn: EarnEvent | null
): QualityRisk {
  const signals: number[] = [];
  if (latestNps) {
    // Promoter=0, Passive=1, Detractor=2
    signals.push(latestNps.category === 'promoter' ? 0 : latestNps.category === 'passive' ? 1 : 2);
  }
  if (latestEarn) {
    // score 80+ = 0, 60–79 = 1, <60 = 2
    signals.push(latestEarn.score0to100 >= 80 ? 0 : latestEarn.score0to100 >= 60 ? 1 : 2);
  }
  if (signals.length === 0) return 'none';
  const avg = signals.reduce((a, b) => a + b, 0) / signals.length;
  if (avg < 0.75) return 'good';
  if (avg < 1.5) return 'watch';
  return 'at_risk';
}

export function sortTimeline(events: QualityTimelineEvent[]): QualityTimelineEvent[] {
  return [...events].sort((a, b) => {
    const tDiff = b.timestamp.localeCompare(a.timestamp);
    return tDiff !== 0 ? tDiff : a.id.localeCompare(b.id);
  });
}
